import { FormEvent } from 'react';
import { IListItem, INewDonorRecipientBaseData } from '@nkr/models/index';
import { IBaseDataValidation, TypeOption } from './data-model';
import { Col, ControlLabel, FormControl, FormGroup, HelpBlock, OverlayTrigger, Popover, Row } from 'react-bootstrap'
import { MedicalRecordLinkEmail } from './medical-record-link-email';
import YesNoUnknownRadio, { YesNoUnknown } from '@nkr/components/form/yes-no-unknown-radio';
import MaskedInput from 'react-maskedinput';

interface IProps {
    centerId: number,
    type: TypeOption,
    data: INewDonorRecipientBaseData,
    validation: IBaseDataValidation,
    genderList: IListItem[],
    bloodTypeList: IListItem[],
    stateList: IListItem[],
    onFieldChange: (name: string, value: any) => void,
}

const DonorDetails = (props: IProps) => {
    let { alias, mrn, gender, dateOfBirth, email, phoneNumber, firstName, lastName, bloodType, hasNutcracker, address, city, state, postalCode, shipTypingSwabs, sendSelfRegistrationInvite, medicalRecordId } = props.data;

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        
        if (['shipTypingSwabs', 'sendSelfRegistrationInvite'].indexOf(name) >= 0) {
            props.onFieldChange(name, parseInt(value));
        } else {
            props.onFieldChange(name, value);
        }
    };
    const handleYesNoUnknownChange = (name: string, value: number) => {
        props.onFieldChange(name, value ?? -1);
    }

    return (
        <>
            <MedicalRecordLinkEmail
                centerId={props.centerId}
                email={email}
                medicalRecordId={medicalRecordId}
                validation={props.validation}
                disabled={false}
                showMedicalRecord={true}
                onFieldChange={props.onFieldChange}
            />

            <Row>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.alias == false || props.validation.existingAlias ? 'error' : null}>
                        <ControlLabel>Alias</ControlLabel>
                        <FormControl
                            name='alias'
                            value={alias}
                            onChange={handleFieldChange}
                            minLength={6}
                            maxLength={12}
                        />

                        {props.validation.existingAlias && (
                            <HelpBlock>Alias must be unique</HelpBlock>
                        )}

                        <HelpBlock>Letters and numbers only; minimum 6, maximum 12</HelpBlock>
                    </FormGroup>
                </Col>

                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.mrn == false || props.validation.existingMrn ? 'error' : null}>
                        <ControlLabel>MRN</ControlLabel>
                        <FormControl
                            name='mrn'
                            value={mrn}
                            onChange={handleFieldChange}
                            maxLength={24}
                        />

                        {props.validation.existingMrn && (
                            <HelpBlock>The MRN is not unique. There is already a donor record with this MRN. If the donor is deleted, contact Ops.</HelpBlock>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.gender == false ? 'error' : null}>
                        <ControlLabel>Gender</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='gender'
                            value={gender}
                            onChange={handleFieldChange}>
                            <option value={''}>- Please Select -</option>
                            {props.genderList.map(item =>
                                <option key={'gender-' + item.id} value={item.abbreviation}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                </Col>

                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.dateOfBirth == false ? 'error' : null}>
                        <ControlLabel>Date of Birth</ControlLabel>
                        <MaskedInput
                            name='dateOfBirth'
                            className='form-control'
                            mask='11/11/1111'
                            value={dateOfBirth}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.firstName == false ? 'error' : null}>
                        <ControlLabel>First Name</ControlLabel>
                        <FormControl
                            name='firstName'
                            value={firstName}
                            onChange={handleFieldChange} />
                    </FormGroup>
                </Col>

                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.lastName == false ? 'error' : null}>
                        <ControlLabel>Last Name</ControlLabel>
                        <FormControl
                            name='lastName'
                            value={lastName}
                            onChange={handleFieldChange} />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.bloodType == false ? 'error' : null}>
                        <ControlLabel>Blood Type</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='bloodType'
                            value={bloodType}
                            onChange={handleFieldChange}>
                            <option value={''}>- Please Select -</option>
                            {props.bloodTypeList.map(item =>
                                <option key={'abo-' + item.id} value={item.abbreviation}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                </Col>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.phoneNumber == false ? 'error' : null}>
                        <ControlLabel>Phone Number</ControlLabel>
                        <FormControl
                            name='phoneNumber'
                            value={phoneNumber}
                            onChange={handleFieldChange} />
                        {props.validation.phoneNumber === false && (
                            <HelpBlock>Please enter a valid phone number.</HelpBlock>
                        )}
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.hasNutcracker == false ? 'error' : null}>
                        <ControlLabel>Has Nutcracker Syndrome? <span style={{ color: 'red' }}>*</span>&nbsp;
                            <OverlayTrigger trigger={['hover', 'click']} placement='right'
                                overlay={<Popover>Nutcracker syndrome is a rare vascular condition characterized by compression of the left renal vein between the aorta and the superior mesenteric artery causing symptoms. When this anatomical finding does not produce symptoms, it is referred to as Nutcracker Phenomenon.</Popover>}>
                                <span className='glyphicon glyphicon-info-sign'></span>
                            </OverlayTrigger>
                        </ControlLabel>
                        <YesNoUnknownRadio
                            name='hasNutcracker'
                            value={hasNutcracker}
                            onChange={props.onFieldChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={12} lgOffset={0}>
                    <FormGroup validationState={props.validation.address == false ? 'error' : null}>
                        <ControlLabel>Address</ControlLabel>
                        <FormControl
                            name='address'
                            value={address}
                            onChange={handleFieldChange} />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={5} lgOffset={0}>
                    <FormGroup validationState={props.validation.city == false ? 'error' : null}>
                        <ControlLabel>City</ControlLabel>
                        <FormControl
                            name='city'
                            value={city}
                            onChange={handleFieldChange} />
                    </FormGroup>
                </Col>

                <Col xs={12} lg={4} lgOffset={0}>
                    <FormGroup validationState={props.validation.state == false ? 'error' : null}>
                        <ControlLabel>State</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='state'
                            value={state}
                            onChange={handleFieldChange}>
                            <option value={''}>- Please Select -</option>
                            {props.stateList.map(item =>
                                <option key={'state-' + item.id} value={item.abbreviation}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                </Col>

                <Col xs={12} lg={3} lgOffset={0}>
                    <FormGroup validationState={props.validation.postalCode == false ? 'error' : null}>
                        <ControlLabel>Postal Code</ControlLabel>
                        <FormControl
                            name='postalCode'
                            value={postalCode}
                            onChange={handleFieldChange} />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} lg={12} lgOffset={0}>
                    <FormGroup validationState={props.validation.shipTypingSwabs == false ? 'error' : null}>
                        <ControlLabel>High Resolution Tissue Typing (Cheek Swab) <span style={{ color: 'red' }}>*</span></ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='shipTypingSwabs'
                            style={{ width: 'initial' }}
                            value={shipTypingSwabs}
                            onChange={handleFieldChange}>
                            <option value={-1}>- Please Select -</option>
                            <option value={1}>Order High Res Tissue Typing (Cheek Swab)</option>
                            <option value={2}>Utilize a Bulk Cheek Swab (Kit On-hand)</option>
                        </FormControl>
                    </FormGroup>
                </Col>
            </Row>

            {(props.type == TypeOption.DonorOnlyFv || props.type == TypeOption.DonorOnlySv) && (
                <Row>
                    <Col xs={12} lg={6} lgOffset={0}>
                        <FormGroup>
                            <ControlLabel>Send Voucher Self-Registration E-Mail to donor?</ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='sendSelfRegistrationInvite'
                                value={sendSelfRegistrationInvite}
                                onChange={handleFieldChange}>
                                <option value={-1}>- Please Select -</option>
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>
            )}
        </>
    );
}

export default DonorDetails;